import { useEffect, useState } from "react";
import type { Deal, Stage } from "../types";
import { calc } from "../engine/engine";
import { money0 } from "../lib/format";
import {
  listContacts, saveContact, deleteContact, blankContact, contactsForDeal,
  CONTACT_TYPES, type Contact, type ContactType,
} from "../lib/contacts";

const STAGES: Stage[] = ["Prospect", "Screening", "Underwriting", "Offer", "Under Contract", "Closed"];

/**
 * Beacon's native CRM — YOUR source of truth. A pipeline board over your deals
 * and a contact book, both on your own database. GoHighLevel only mirrors this.
 */
export function Crm({ deals, onOpen, onStage }: { deals: Deal[]; onOpen: (id: string) => void; onStage: (id: string, s: Stage) => void }) {
  const [tab, setTab] = useState<"pipeline" | "contacts">("pipeline");
  return (
    <div className="wrap">
      <div className="pe">
        <div className="l">◆ Your CRM — on your own database</div>
        <p>
          This is Beacon's <b>native</b> pipeline and contact book. It's the <b>source of truth</b> you own — GoHighLevel
          only ever holds a downstream copy. Move deals through your pipeline, keep every seller, agent, lender and
          contractor in one place.
        </p>
      </div>
      <div className="crm-tabs">
        <button className={tab === "pipeline" ? "on" : ""} onClick={() => setTab("pipeline")}>🗂 Pipeline</button>
        <button className={tab === "contacts" ? "on" : ""} onClick={() => setTab("contacts")}>📇 Contacts</button>
      </div>
      {tab === "pipeline" ? <PipelineBoard deals={deals} onOpen={onOpen} onStage={onStage} /> : <ContactsPanel deals={deals} />}
    </div>
  );
}

// --- pipeline board (kanban over deal.stage) --------------------------------
function PipelineBoard({ deals, onOpen, onStage }: { deals: Deal[]; onOpen: (id: string) => void; onStage: (id: string, s: Stage) => void }) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<Stage | null>(null);

  const drop = (stage: Stage) => (e: React.DragEvent) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragId;
    if (id) onStage(id, stage);
    setDragId(null); setOver(null);
  };

  return (
    <div className="kanban">
      {STAGES.map((stage) => {
        const col = deals.filter((d) => d.stage === stage);
        const value = col.reduce((s, d) => s + calc(d).stabVal, 0);
        return (
          <div
            key={stage}
            className={"kcol" + (over === stage ? " over" : "")}
            onDragOver={(e) => { e.preventDefault(); setOver(stage); }}
            onDragLeave={() => setOver((o) => (o === stage ? null : o))}
            onDrop={drop(stage)}
          >
            <div className="kcol-head">
              <span>{stage}</span>
              <span className="kcount">{col.length}</span>
            </div>
            <div className="kcol-sub">{col.length ? money0(value) : "—"}</div>
            <div className="kcards">
              {col.map((d) => {
                const s = calc(d).score;
                return (
                  <div
                    key={d.id}
                    className="kcard"
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", d.id); setDragId(d.id); }}
                    onDragEnd={() => { setDragId(null); setOver(null); }}
                    onClick={() => onOpen(d.id)}
                  >
                    <div className="kcard-top">
                      <span className="kcard-name">{d.name}</span>
                      <span className={"tag t-" + (s.cls === "pass" ? "pass" : s.cls === "watch" ? "watch" : "fail")}>{s.gated ? "GATED" : s.composite}</span>
                    </div>
                    <div className="kcard-addr">{d.addr || d.market}</div>
                    <div className="kcard-val">{money0(calc(d).stabVal)}</div>
                  </div>
                );
              })}
              {col.length === 0 && <div className="kempty">Drop deals here</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- contacts ---------------------------------------------------------------
const TYPE_CLS: Record<ContactType, string> = {
  Seller: "t-watch", Agent: "t-pass", Wholesaler: "t-watch", Lender: "t-pass", Attorney: "t-watch", Contractor: "t-pass", Other: "t-watch",
};

function ContactsPanel({ deals }: { deals: Deal[] }) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [editing, setEditing] = useState<Contact | null>(null);
  const dealName = (id: string) => deals.find((d) => d.id === id)?.name ?? "—";

  useEffect(() => { listContacts().then(setContacts).catch(() => {}); }, []);

  async function save() {
    if (!editing || !editing.name.trim()) return;
    await saveContact(editing);
    setContacts(await listContacts());
    setEditing(null);
  }
  async function remove(id: string) {
    await deleteContact(id);
    setContacts(await listContacts());
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <h2 className="sechead" style={{ fontSize: 18, margin: 0 }}>Contacts — {contacts.length}</h2>
        {!editing && <button className="btn btn-p btn-sm" onClick={() => setEditing(blankContact())}>＋ Add contact</button>}
      </div>

      {editing && (
        <div className="card pad" style={{ marginBottom: 14, borderLeft: "4px solid var(--brass)" }}>
          <div className="eyebrow">{contacts.some((c) => c.id === editing.id) ? "Edit contact" : "New contact"}</div>
          <div className="bb-grid" style={{ marginTop: 10 }}>
            <label className="bb-f"><span>Name</span><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Full name" /></label>
            <label className="bb-f"><span>Type</span>
              <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value as ContactType })}>
                {CONTACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></label>
            <label className="bb-f"><span>Company</span><input value={editing.company ?? ""} onChange={(e) => setEditing({ ...editing, company: e.target.value })} /></label>
            <label className="bb-f"><span>Email</span><input type="email" value={editing.email ?? ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></label>
            <label className="bb-f"><span>Phone</span><input value={editing.phone ?? ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></label>
          </div>
          <label className="bb-f" style={{ marginTop: 10 }}><span>Notes</span>
            <input value={editing.notes ?? ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="context, how you met, next step" /></label>
          {deals.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="bb-f" style={{ marginBottom: 6 }}><span>Linked deals</span></div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {deals.map((d) => {
                  const on = editing.dealIds.includes(d.id);
                  return (
                    <button key={d.id} className={"chip-toggle" + (on ? " on" : "")}
                      onClick={() => setEditing({ ...editing, dealIds: on ? editing.dealIds.filter((x) => x !== d.id) : [...editing.dealIds, d.id] })}>
                      {on ? "✓ " : "＋ "}{d.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn btn-a btn-sm" onClick={save} disabled={!editing.name.trim()}>Save contact</button>
            <button className="btn btn-g btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card pad">
        {contacts.length === 0 ? (
          <div className="note">No contacts yet. Add your sellers, agents, lenders, attorneys and contractors — and link them to the deals they touch.</div>
        ) : (
          <div className="tablewrap">
            <table className="t">
              <thead><tr><th>Name</th><th>Type</th><th>Company</th><th>Contact</th><th>Linked deals</th><th></th></tr></thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600, color: "var(--forest)" }}>{c.name}{c.notes && <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 400 }}>{c.notes}</div>}</td>
                    <td><span className={"tag " + TYPE_CLS[c.type]}>{c.type}</span></td>
                    <td>{c.company || "—"}</td>
                    <td style={{ fontSize: 12 }}>
                      {c.email && <div>{c.email}</div>}
                      {c.phone && <div style={{ color: "var(--muted)" }}>{c.phone}</div>}
                      {!c.email && !c.phone && "—"}
                    </td>
                    <td style={{ fontSize: 12 }}>{c.dealIds.length ? c.dealIds.map(dealName).join(", ") : <span style={{ color: "var(--muted)" }}>—</span>}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="btn btn-g btn-sm" style={{ padding: "3px 8px" }} onClick={() => setEditing(c)}>Edit</button>
                      <button className="rp-x" title="Delete" onClick={() => remove(c.id)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
